import { GridColumnMenuItemProps, useGridApiContext, useGridRootProps } from '@mui/x-data-grid-premium';
import React, { ChangeEvent } from 'react';
import { useI18n } from '../../../../i18n/I18n';
import { Icons } from '../../../../icons/Icons';
import { KEY_CREDITS_COLUMN_MENU_SELECT_MIN_WIDTH, KEY_CREDITS_DISPLAY_MODE } from '../../../constants';
import type { KeyCreditsDisplayMode } from '../../../types';
import {
    isKeyCreditsColumnField,
    stopKeyCreditsColumnMenuItemClick,
    stopKeyCreditsColumnMenuSelectKeyDown
} from '../../utils';

export interface KeyCreditsColumnMenuDisplayModeItemProps extends GridColumnMenuItemProps {
    displayModeColumnNames: string[];
    displayMode: Record<string, KeyCreditsDisplayMode>;
    onDisplayModeChange: (field: string, mode: KeyCreditsDisplayMode) => void;
}

export default function KeyCreditsColumnMenuDisplayModeItem({
    colDef,
    displayModeColumnNames,
    displayMode,
    onDisplayModeChange
}: KeyCreditsColumnMenuDisplayModeItemProps) {
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const t = useI18n();
    const fieldName = colDef?.field ?? '';

    if (!isKeyCreditsColumnField(fieldName, displayModeColumnNames)) {
        return null;
    }

    const currentDisplayMode = displayMode[fieldName] ?? KEY_CREDITS_DISPLAY_MODE.lineBreak;
    const isBaseSelectNative = rootProps.slotProps?.baseSelect?.native ?? false;

    const handleDisplayModeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedMode = event.target?.value as KeyCreditsDisplayMode;
        if (!selectedMode || selectedMode === currentDisplayMode) return;
        onDisplayModeChange(fieldName, selectedMode);
        apiRef.current.hideColumnMenu();
    };

    return (
        <rootProps.slots.baseMenuItem
            onClick={stopKeyCreditsColumnMenuItemClick}
            iconStart={<Icons.Account fontSize="small" />}
        >
            <rootProps.slots.baseSelect
                id={`key-credits-display-mode-${fieldName}`}
                value={currentDisplayMode}
                label={t('common.display.mode')}
                onChange={handleDisplayModeChange}
                onKeyDown={stopKeyCreditsColumnMenuSelectKeyDown}
                onClick={stopKeyCreditsColumnMenuItemClick}
                onBlur={(event) => event.stopPropagation()}
                native={isBaseSelectNative}
                fullWidth
                size="small"
                style={{ minWidth: KEY_CREDITS_COLUMN_MENU_SELECT_MIN_WIDTH }}
            >
                <rootProps.slots.baseSelectOption native={isBaseSelectNative} value={KEY_CREDITS_DISPLAY_MODE.comma}>
                    {t('common.comma')}
                </rootProps.slots.baseSelectOption>
                <rootProps.slots.baseSelectOption native={isBaseSelectNative} value={KEY_CREDITS_DISPLAY_MODE.chip}>
                    {t('common.chip')}
                </rootProps.slots.baseSelectOption>
                <rootProps.slots.baseSelectOption native={isBaseSelectNative} value={KEY_CREDITS_DISPLAY_MODE.pipe}>
                    {t('common.pipe')}
                </rootProps.slots.baseSelectOption>
                <rootProps.slots.baseSelectOption
                    native={isBaseSelectNative}
                    value={KEY_CREDITS_DISPLAY_MODE.lineBreak}
                >
                    {t('common.line.break')}
                </rootProps.slots.baseSelectOption>
            </rootProps.slots.baseSelect>
        </rootProps.slots.baseMenuItem>
    );
}
